import { useState, useEffect } from 'react';
import { 
  Tabs, 
  Card, 
  Text, 
  Group, 
  Stack, 
  Grid, 
  Badge, 
  Progress, 
  Loader,
  Alert
} from '@mantine/core';
import { 
  IconUsers, 
  IconBuildingStore, 
  IconExclamationCircle,
  IconChartBar
} from '@tabler/icons-react';
import { api } from '../../services/api';

const AdminStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAdminStats();
      setStats(response);
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setError(err.response?.data?.error || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Tabs.Panel value="stats" pt="xs">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Tabs.Panel>
    );
  }

  if (error) {
    return (
      <Tabs.Panel value="stats" pt="xs">
        <Alert 
          icon={<IconExclamationCircle size="1rem" />} 
          color="red" 
          title="Error loading statistics"
        >
          {error}
        </Alert>
      </Tabs.Panel>
    );
  }

  const userPercentages = stats ? {
    active: (stats.users.active / stats.users.total * 100) || 0,
    staff: (stats.users.staff / stats.users.total * 100) || 0,
  } : { active: 0, staff: 0 };

  const sellerPercentages = stats ? {
    approved: (stats.sellers.approved / stats.sellers.total * 100) || 0,
    verified: (stats.sellers.verified / stats.sellers.total * 100) || 0,
  } : { approved: 0, verified: 0 };

  return (
    <Tabs.Panel value="stats" pt="xs">
      <Grid>
        {/* Users Overview */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group align="apart" mb="xs">
              <Text fw={500} size="lg">
                <IconUsers size="1.2rem" style={{ marginRight: '0.5rem' }} />
                Users Overview
              </Text>
            </Group>

            <Stack gap="md">
              <Group align="apart">
                <Text size="sm" c="dimmed">Total Users</Text>
                <Badge variant="light" size="lg">{stats?.users.total || 0}</Badge>
              </Group>

              <div>
                <Group align="apart" mb={5}>
                  <Text size="sm">Active Users</Text>
                  <Text size="sm">{stats?.users.active || 0} ({userPercentages.active.toFixed(1)}%)</Text>
                </Group>
                <Progress value={userPercentages.active} size="sm" />
              </div>

              <div>
                <Group align="apart" mb={5}>
                  <Text size="sm">Staff Members</Text>
                  <Text size="sm">{stats?.users.staff || 0} ({userPercentages.staff.toFixed(1)}%)</Text>
                </Group>
                <Progress value={userPercentages.staff} size="sm" color="orange" />
              </div>

              <Group align="apart">
                <Text size="sm" c="dimmed">Recent Registrations (30 days)</Text>
                <Badge variant="outline" color="green">{stats?.users.recent_registrations || 0}</Badge>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Sellers Overview */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group align="apart" mb="xs">
              <Text fw={500} size="lg">
                <IconBuildingStore size="1.2rem" style={{ marginRight: '0.5rem' }} />
                Sellers Overview
              </Text>
            </Group>

            <Stack gap="md">
              <Group align="apart">
                <Text size="sm" c="dimmed">Total Sellers</Text>
                <Badge variant="light" size="lg">{stats?.sellers.total || 0}</Badge>
              </Group>

              <div>
                <Group align="apart" mb={5}>
                  <Text size="sm">Approved Sellers</Text>
                  <Text size="sm">{stats?.sellers.approved || 0} ({sellerPercentages.approved.toFixed(1)}%)</Text>
                </Group>
                <Progress value={sellerPercentages.approved} size="sm" color="green" />
              </div>

              <div>
                <Group align="apart" mb={5}>
                  <Text size="sm">Verified Sellers</Text>
                  <Text size="sm">{stats?.sellers.verified || 0} ({sellerPercentages.verified.toFixed(1)}%)</Text>
                </Group>
                <Progress value={sellerPercentages.verified} size="sm" color="blue" />
              </div>

              <Group align="apart">
                <Text size="sm" c="dimmed">Pending Verification</Text>
                <Badge variant="outline" color="yellow">{stats?.sellers.pending || 0}</Badge>
              </Group>

              <Group align="apart">
                <Text size="sm" c="dimmed">Recent Applications (30 days)</Text>
                <Badge variant="outline" color="green">{stats?.sellers.recent_applications || 0}</Badge>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Platform Overview */}
        <Grid.Col span={12}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group align="apart" mb="xs">
              <Text fw={500} size="lg">
                <IconChartBar size="1.2rem" style={{ marginRight: '0.5rem' }} />
                Platform Overview
              </Text>
            </Group>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Total Accounts</Text>
                  <Text size="xl" fw={700}>{stats?.overview.total_accounts || 0}</Text>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Seller Percentage</Text>
                  <Text size="xl" fw={700}>{stats?.overview.seller_percentage || 0}%</Text>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 4 }}>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Pending Verifications</Text>
                  <Text size="xl" fw={700} c={stats?.overview.verification_pending > 0 ? "orange" : "green"}>
                    {stats?.overview.verification_pending || 0}
                  </Text>
                </Stack>
              </Grid.Col>
            </Grid>
          </Card>
        </Grid.Col>
      </Grid>
    </Tabs.Panel>
  );
};

export default AdminStats;